import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Clock, Users } from "lucide-react";

export default function AmenitiesPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(new Date());
  const [start, setStart] = useState("10:00");
  const [end, setEnd] = useState("11:00");
  const [guests, setGuests] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => { if (propertyId) (async () => {
    const { data } = await api.get(`/amenities?property_id=${propertyId}`);
    setItems(data.items);
  })(); }, [propertyId]);

  const book = async () => {
    await api.post("/bookings", {
      amenity_id: selected.id,
      date: date.toISOString().slice(0,10),
      start_time: start,
      end_time: end,
      guests: Number(guests) || 0,
    });
    toast.success(selected.requires_approval ? "Booking submitted for approval" : "Booking confirmed");
    setOpen(false);
  };

  return (
    <div>
      <PageHeader eyebrow="Community" title="Amenities" subtitle="Book the gym, pool, lounge, and more." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => (
          <Card key={a.id} className="overflow-hidden bg-white">
            <div className="h-40 relative bg-zinc-100">
              <img src={a.image} alt={a.name} className="w-full h-full object-cover" />
              {a.requires_approval && <Badge className="absolute top-2 right-2 bg-amber-500 text-white">Approval required</Badge>}
            </div>
            <div className="p-4">
              <div className="text-lg font-semibold font-display">{a.name}</div>
              <div className="mt-2 text-xs text-zinc-500 flex items-center gap-3">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {a.hours}</span>
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {a.capacity}</span>
              </div>
              <Button
                data-testid={`book-${a.id}`}
                onClick={() => { setSelected(a); setOpen(true); }}
                className="w-full mt-4 rounded-full bg-zinc-900 hover:bg-zinc-800"
              >Book</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Book · {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Date</Label>
              <div className="mt-1 border rounded-md">
                <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} className="p-2" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></div>
              <div><Label>End</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
            </div>
            <div><Label>Guests</Label><Input type="number" min="0" value={guests} onChange={(e) => setGuests(e.target.value)} /></div>
            <Button data-testid="book-confirm" onClick={book} className="w-full">Confirm booking</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
